import { Bell, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { InviteBoardStatus } from "@graftus/schema";
import { isTeamReady } from "@graftus/schema";
import { cn } from "@/lib/utils";
import { initialsOf } from "@/components/ui/avatar-stack";

/** Small building blocks of the done screen: stat cards, the per-person list, team bars, reminders. */

export function StatCard({ label, value, tone }: { label: string; value: number; tone: "success" | "primary" | "info" | "muted" | "warning" }) {
  return (
    <div className="flex flex-col gap-1 rounded-g-lg border border-g-line bg-g-bg-card p-4">
      <span className="text-[11px] font-bold uppercase tracking-wide text-g-ink-500">{label}</span>
      <span
        className={cn(
          "text-[28px] font-extrabold leading-none tabular-nums",
          tone === "success" && "text-g-success",
          tone === "primary" && "text-g-primary",
          tone === "info" && "text-g-info",
          tone === "warning" && "text-g-warning",
          tone === "muted" && "text-g-ink-400",
        )}
      >
        {value}
      </span>
    </div>
  );
}

const STATUS_TONES: Record<InviteBoardStatus, string> = {
  scheduled: "bg-g-warning-tint text-g-warning",
  invited: "bg-g-bg-track text-g-ink-600",
  opened: "bg-g-info-tint text-g-info",
  started: "bg-g-primary-tint text-g-primary",
  done: "bg-g-success-tint text-g-success",
  expired: "bg-g-bg-track text-g-ink-400",
  cancelled: "bg-g-bg-track text-g-ink-400",
  error: "bg-g-danger-tint text-g-danger",
};

export function StatusPill({ status }: { status: InviteBoardStatus }) {
  const { t } = useTranslation("onboarding");
  return (
    <span className={cn("inline-flex rounded-g-pill px-2.5 py-0.5 text-[12px] font-bold", STATUS_TONES[status])}>
      {t(`done.status.${status}`)}
    </span>
  );
}

export interface StatusRow {
  id: string;
  name: string;
  team: string | null;
  status: InviteBoardStatus;
}

export function StatusList({ rows }: { rows: StatusRow[] }) {
  return (
    <ul className="divide-y divide-g-line-row rounded-g-lg border border-g-line bg-g-bg-card" data-testid="onboarding-status-list">
      {rows.map((row) => (
        <li key={row.id} className="flex items-center gap-3 px-4 py-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-g-pill bg-g-primary-tint text-[11px] font-bold text-g-primary-press" aria-hidden="true">
            {initialsOf(row.name)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[14px] font-semibold text-g-ink-900">{row.name}</span>
            {row.team ? <span className="truncate text-[12px] text-g-ink-500">{row.team}</span> : null}
          </span>
          <StatusPill status={row.status} />
        </li>
      ))}
    </ul>
  );
}

export interface TeamBar {
  id: string;
  name: string;
  done: number;
  total: number;
}

export function TeamProfileBars({ teams }: { teams: TeamBar[] }) {
  const { t } = useTranslation("onboarding");
  return (
    <ul className="flex flex-col gap-3" data-testid="onboarding-team-bars">
      {teams.map((team) => {
        const ready = isTeamReady(team.done, team.total);
        const pct = team.total > 0 ? Math.round((team.done / team.total) * 100) : 0;
        return (
          <li key={team.id} className="flex flex-col gap-1.5">
            <span className="flex items-center justify-between gap-3 text-[13.5px]">
              <span className="font-semibold text-g-ink-900">{team.name}</span>
              <span className={cn("font-semibold", ready ? "text-g-success" : "text-g-ink-500")}>
                {ready ? t("done.teamReady") : t("done.teamProgress", { done: team.done, total: team.total })}
              </span>
            </span>
            <span className="relative block h-2 overflow-hidden rounded-g-pill bg-g-bg-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={team.name}>
              <span className={cn("absolute inset-y-0 left-0 rounded-g-pill transition-all duration-300", ready ? "bg-g-success-fill" : "bg-g-primary")} style={{ width: `${pct}%` }} />
              <span className="absolute inset-y-0 w-px bg-g-ink-400" style={{ left: "70%" }} aria-hidden="true" />
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function RemindersList({ enabled, mode }: { enabled: boolean; mode: "sent" | "scheduled" }) {
  const { t } = useTranslation("onboarding");
  const rows = enabled
    ? [
        { title: t("done.reminders.first"), when: mode === "sent" ? t("done.reminders.inDays", { n: 3 }) : t("done.reminders.afterDays", { n: 3 }) },
        { title: t("done.reminders.second"), when: mode === "sent" ? t("done.reminders.inDays", { n: 7 }) : t("done.reminders.afterDays", { n: 7 }) },
        { title: t("done.reminders.expiry"), when: t("done.reminders.afterDays", { n: 14 }) },
      ]
    : [{ title: t("done.reminders.off"), when: "" }];
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li key={row.title} className="flex items-center gap-3 rounded-g-md border border-g-line bg-g-bg-card px-3 py-2 text-[13.5px]">
          {enabled ? <Bell className="h-4 w-4 text-g-primary" aria-hidden="true" /> : <Clock className="h-4 w-4 text-g-ink-400" aria-hidden="true" />}
          <span className="flex-1 font-medium text-g-ink-900">{row.title}</span>
          <span className="text-g-ink-500">{row.when}</span>
        </li>
      ))}
    </ul>
  );
}
